import { X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopilotChat } from "@copilotkit/react-ui";
import { useEffect } from "react";
import "./AskEmmaChat.css";

interface AskEmmaChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const suggestions = [
  {
    title: "Who are my most valuable customers?",
    message: "Who are my most valuable customers?"
  },
  {
    title: "Show me sales data for the last 30 days",
    message: "Show me sales data for the last 30 days"
  },
  {
    title: "How can I create an SMS campaign?",
    message: "How can I create an SMS campaign?"
  },
  {
    title: "What products are selling best?",
    message: "What products are selling best?"
  }
];

export function AskEmmaChat({ isOpen, onClose }: AskEmmaChatProps) {
  useEffect(() => {
    if (!isOpen) return;

    const updateBackground = () => {
      const chatElement = document.querySelector(
        ".emma-chat-wrapper .copilotKitChat"
      ) as HTMLElement | null;

      if (chatElement) {
        chatElement.style.backgroundColor = "#ffffff";
        chatElement.style.setProperty(
          "--copilot-kit-background-color",
          "#ffffff",
          "important"
        );
      }
    };

    const observer = new window.MutationObserver(updateBackground);
    const wrapper = document.querySelector(".emma-chat-wrapper");

    if (wrapper) {
      observer.observe(wrapper, { childList: true, subtree: true });
    }

    updateBackground();
    const t1 = setTimeout(updateBackground, 100);
    const t2 = setTimeout(updateBackground, 500);

    return () => {
      observer.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isOpen]);

  return (
    <div
      className={`fixed right-0 top-0 h-full w-0 bg-white border-l shadow-lg transition-all duration-300 ease-in-out flex flex-col z-50 ${
        isOpen ? "w-96" : ""
      }`}
    >
      {/* Header */}
      <div className="h-20 flex items-center justify-between px-6 border-b bg-white">
        <div className="flex items-center gap-3">
          <img
            src="/emma.png"
            alt="Emma"
            className="w-10 h-10 rounded-full object-cover border"
          />
          <div>
            <h2 className="text-base font-semibold flex items-center gap-1.5">
              Chat with Emma
              <Sparkles className="h-4 w-4 text-teal-500 fill-teal-500" />
            </h2>
            <p className="text-xs text-gray-500">
              Your friendly business assistant
            </p>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 rounded-full"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-white p-4 space-y-4">
        {/* Initial Message */}
        <div className="flex items-start gap-2.5">
          <img
            src="/emma.png"
            alt="Emma"
            className="w-8 h-8 rounded-full border"
          />
          <div className="bg-gray-100 rounded-lg px-4 py-3 max-w-[85%]">
            <p className="text-sm text-gray-800">
              Hi there! 👋 I'm Emma, your business buddy. Ask me anything about
              growing your business, improving campaigns, or understanding your
              customers better!
            </p>
          </div>
        </div>

        {/* Suggestions (manual, always visible) */}
        <div className="grid grid-cols-1 gap-2">
          {suggestions.map((s) => (
            <button
              key={s.message}
              className="text-left p-3 rounded-lg border hover:bg-gray-50 text-sm"
              onClick={() => {
                const input = document.querySelector<HTMLTextAreaElement>(
                  ".copilotKitChat textarea"
                );

                if (input) {
                  // Properly update React-controlled textarea value so CopilotKit
                  // internal state (including the send button enabled state)
                  // is kept in sync. Directly assigning `input.value` is not
                  // always detected by React.
                  const prototype = window.HTMLTextAreaElement.prototype;
                  const descriptor = Object.getOwnPropertyDescriptor(
                    prototype,
                    "value"
                  );

                  descriptor?.set?.call(input, s.message);

                  input.dispatchEvent(
                    new window.Event("input", { bubbles: true })
                  );

                  input.focus();
                }
              }}
            >
              {s.title}
            </button>
          ))}
        </div>

        {/* Copilot Chat */}
        <div className="emma-chat-wrapper">
          <CopilotChat
            instructions={`You are Emma, a friendly business assistant.
Use actions to fetch real data whenever possible.`}
            labels={{
              placeholder: "Ask anything about your business..."
            }}
          />
        </div>
      </div>
    </div>
  );
}
